<template>
  <div class="page-container">
    <TopBar title="Overview"></TopBar>

    <!-- Loading -->
    <div v-if="isLoading" class="container w-max">
      <div class="flex flex-col items-center">
        <img class="h-36 mt-20" src="@/assets/img/loading.svg" />
        <h2>Loading</h2>
      </div>
    </div>

    <!-- Date Modal -->
    <div v-if="showDateModal" class="modal overflow-auto">
      <div class="card">
        <div class="header">
          <div class="title">
            <div class="flex flex-row space-x-6">
              <button
                type="button"
                :class="selectedTempDate === presetDate.today ? 'date-selection selected' : 'date-selection'"
                @click="setDateRange(presetDate.today)"
              >
                Today
              </button>
              <button
                type="button"
                :class="selectedTempDate === presetDate.past7Days ? 'date-selection selected' : 'date-selection'"
                @click="setDateRange(presetDate.past7Days)"
              >
                Past 7 days
              </button>
              <button
                type="button"
                :class="selectedTempDate === presetDate.past30Days ? 'date-selection selected' : 'date-selection'"
                @click="setDateRange(presetDate.past30Days)"
              >
                Past 30 days
              </button>
            </div>
          </div>
          <div
            class="close"
            @click="
              showDateModal = false;
              tempRange = null;
              selectedTempDate = null;
            "
          >
            <i class="icn icn-x sm"></i>
          </div>
        </div>
        <div class="content pt-6">
          <div class="flex flex-row bg-primary-100 items-center rounded">
            <div class="flex items-center mr-2 bg-primary-500 py-2 px-2 rounded-l text-white">
              <i class="icn icn-information-circle"></i>
            </div>
            <div class="py-2 pr-2 text-primary-500">Displaying in local timezone</div>
          </div>
          <div class="flex flex-col justify-center items-center h-full mt-4">
            <v-date-picker
              ref="calendar"
              v-model="tempRange"
              color="green"
              :columns="2"
              is-range
              @dayclick="selectDateRange()"
              :max-date="getMaxDate()"
            />
          </div>
        </div>

        <div class="flex flex-row justify-center items-center mb-6">
          <button type="button" @click="loadReporting()">Confirm</button>
        </div>
      </div>
    </div>

    <!-- Container -->
    <div v-show="!isLoading && reporting && reporting.donations" class="container center">
      <div class="flex flex-row justify-between">
        <div class="flex flex-row bg-yellow-100 items-center rounded">
          <div class="flex items-center mr-2 bg-yellow-300 py-2 px-2 rounded-l text-white">
            <i class="icn icn-exclamation-circle"></i>
          </div>
          <div class="py-2 pr-2 text-yellow-600">
            This page is in beta testing and is advised not to be used for reporting purposes
          </div>
        </div>
        <button
          type="button"
          class="dropdown"
          @click="
            showDateModal = true;
            tempRange = range;
            selectedTempDate = selectedDate;
          "
        >
          <div v-if="selectedDate === presetDate.today">Today</div>
          <div v-else-if="selectedDate === presetDate.past7Days">Past 7 days</div>
          <div v-else-if="selectedDate === presetDate.past30Days">Past 30 days</div>
          <div v-else>{{ selectedDate }}</div>
          <i class="ml-2 icn icn-chevron-down sm"></i>
        </button>
      </div>
      <div v-if="reporting && reporting.donations" class="flex flex-row space-x-6 mt-6">
        <div class="counter-badge">
          <h2>Total Donations</h2>
          <div class="counter">{{ reporting.donations.totalDonations }}</div>
        </div>
        <div class="counter-badge">
          <h2>Single Gift</h2>
          <div class="counter">{{ reporting.donations.singleGifts ? reporting.donations.singleGifts : 0 }}</div>
        </div>
        <div class="counter-badge">
          <h2>Regular Gift</h2>
          <div class="counter">{{ reporting.donations.regularGifts ? reporting.donations.regularGifts : 0 }}</div>
        </div>
        <div class="counter-badge">
          <h2>Failed</h2>
          <div class="counter">{{ reporting.donations.failed }}</div>
        </div>
      </div>
      <div v-if="reporting && reporting.donations" class="table mt-6">
        <div class="inner">
          <table>
            <thead>
              <tr>
                <th @click="sort('transactionDate')">
                  <span v-bind:class="['s' + getSortStatus('transactionDate')]">Donation Products</span>
                </th>
                <th>Donations</th>
                <th>Single Gift</th>
                <th>Regular Gift</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(product, index) in reporting.products" :key="index">
                <td>
                  <div class="text-xl">{{ product.name }}</div>
                  <div>{{ product.type | titlecase }}</div>
                </td>
                <td class="text-xl">{{ product.totalDonations }}</td>
                <td class="text-xl">{{ product.singleGifts }}</td>
                <td class="text-xl">{{ product.regularGifts }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Chart-->
      <div v-show="chartOptions" class="mt-6 bg-white pt-4">
        <div id="chart"></div>
      </div>
    </div>
  </div>
</template>

<script>
import TopBar from '@/components/TopBar.vue';
import ApexCharts from 'apexcharts';
import api from '@/api';
import moment from 'moment-timezone';

export default {
  name: 'Overview',
  components: {
    TopBar,
  },
  data() {
    return {
      isLoading: true,
      showDateModal: false,
      reporting: undefined,
      range: {
        start: new Date(),
        end: new Date(),
      },
      tempRange: null,
      presetDate: {
        today: 'today',
        past7Days: 'past7Days',
        past30Days: 'past30Days',
      },
      selectedDate: 'past7Days',
      selectedTempDate: null,
      chart: null,
      chartOptions: null,
    };
  },
  mounted() {
    this.loadReporting();
  },
  methods: {
    getSortStatus(attribute) {
      if (this.currentSort === attribute && this.currentSortDir === 'desc') {
        return 2;
      } else if (this.currentSort === attribute && this.currentSortDir === 'asc') {
        return 1;
      } else {
        return 0;
      }
    },
    async loadReporting() {
      try {
        this.isLoading = true;
        this.showDateModal = false;

        this.selectedDate = this.selectedTempDate ?? 'past7Days';
        this.range = this.tempRange ?? {
          start: moment().startOf('day').subtract(6, 'days').toISOString(),
          end: moment().endOf('day').toISOString(),
        };
        this.selectedTempDate = null;
        this.tempRange = null;
        this.reporting = await api.reporting.getDonations(this.range.start, this.range.end);

        // Pie chart
        const element = document.querySelector('#chart');
        if (this.reporting.donations.totalDonations > 0 && !!element) {
          const singleGifts = Number.parseInt(this.reporting.donations.singleGifts);
          const regularGifts = Number.parseInt(this.reporting.donations.regularGifts);

          this.chartOptions = {
            chart: {
              type: 'pie',
              height: '300px',
            },
            series: [singleGifts, regularGifts],
            labels: ['Single Gift', 'Regular Gift'],
          };

          if (!this.chart) {
            this.chart = new ApexCharts(element, this.chartOptions);
            this.chart.render();
          } else {
            this.chart.updateSeries(this.chartOptions.series);
          }
        } else {
          this.chartOptions = null;
        }
      } catch (err) {
        console.log(err);
      } finally {
        this.isLoading = false;
      }
    },
    async setDateRange(value) {
      const calendar = this.$refs.calendar;
      const endOfToday = moment().endOf('day').toISOString();

      // Today
      if (value === this.presetDate.today) {
        this.selectedTempDate = this.presetDate.today;
        this.tempRange = {
          start: moment().startOf('day').toISOString(),
          end: endOfToday,
        };
      }

      // Past 7 days
      if (value === this.presetDate.past7Days) {
        this.selectedTempDate = this.presetDate.past7Days;
        this.tempRange = {
          start: moment().startOf('day').subtract(6, 'days').toISOString(),
          end: endOfToday,
        };
      }

      // Past 30 days
      if (value === this.presetDate.past30Days) {
        this.selectedTempDate = this.presetDate.past30Days;
        this.tempRange = {
          start: moment().startOf('day').subtract(29, 'days').toISOString(),
          end: endOfToday,
        };
      }

      await calendar.move(
        { month: new Date(this.tempRange.end).getMonth(), year: new Date(this.tempRange.end).getFullYear() },
        { position: 1, transition: 'slide-h' },
      );
    },
    selectDateRange() {
      this.selectedTempDate = `${moment(this.tempRange.start).format('DD/MM/YYYY')} - ${moment(
        this.tempRange.end,
      ).format('DD/MM/YYYY')}`;

      this.tempRange = {
        start: moment(this.tempRange.start).toISOString(),
        end: moment(this.tempRange.end).toISOString(),
      };

      // Check if temp range matches preset date
      const startOfToday = moment().startOf('day').toISOString();
      const endOfToday = moment().endOf('day').toISOString();
      const past7Days = moment().startOf('day').subtract(6, 'days').toISOString();
      const past30Days = moment().startOf('day').subtract(29, 'days').toISOString();

      if (this.tempRange.end === endOfToday) {
        if (this.tempRange.start === startOfToday) {
          this.selectedTempDate = this.presetDate.today;
        }
        if (this.tempRange.start === past7Days) {
          this.selectedTempDate = this.presetDate.past7Days;
        }
        if (this.tempRange.start === past30Days) {
          this.selectedTempDate = this.presetDate.past30Days;
        }
      }
    },
    getMaxDate() {
      return moment().endOf('day').toDate();
    },
  },
};
</script>

<style lang="scss" scoped>
.counter-badge {
  @apply flex-1 bg-white flex flex-col p-6 justify-between gap-y-2 rounded-lg;

  .counter {
    @apply text-4xl;
  }

  h2 {
    font-family: ui-sans-serif, system-ui !important;
    color: #9FA6B2;
    font-weight: 400;
    font-size: 12px;
    line-height: 24px;
  }
}

.dropdown {
  @apply outline-none bg-white border-gray-300 text-gray-600;

  &:focus {
    @apply outline-none;
  }
}

.date-selection {
  @apply rounded-none flex-1 items-center px-4 py-2 text-sm font-medium text-gray-600 justify-center bg-white shadow-none border-t-0 border-l-0 border-r-0 border-b-2 border-white outline-none;

  &.selected {
    @apply border-primary-500;
  }
}
</style>
